import { db } from '../firebase/config';
import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  limit,
  doc,
  getDoc,
  Timestamp
} from 'firebase/firestore';

export interface ContentReport {
  id: string;
  postId: string;
  reporterId: string;
  reporterEmail: string;
  reason: ReportReason;
  description: string;
  status: ReportStatus;
  priority: ReportPriority;
  createdAt: Date;
  reviewedAt?: Date;
  reviewedBy?: string;
  resolution?: ReportResolution;
  evidence?: string[]; // URLs to screenshots or other evidence
  tags?: string[]; // Additional categorization tags
}

export interface UserBlock {
  id: string;
  blockerId: string;
  blockedUserId: string;
  reason: string;
  createdAt: Date;
  expiresAt?: Date; // Optional expiration date
  isActive: boolean;
}

export interface ModerationAction {
  id: string;
  moderatorId: string;
  targetType: 'post' | 'user' | 'comment';
  targetId: string;
  action: ModerationActionType;
  reason: string;
  details?: string;
  createdAt: Date;
  expiresAt?: Date;
  isActive: boolean;
}

export interface ModerationSettings {
  userId: string;
  autoModeration: boolean;
  reportThreshold: number; // Number of reports before auto-action
  allowedContentTypes: string[];
  blockedKeywords: string[];
  moderationLevel: 'strict' | 'moderate' | 'lenient';
  notificationSettings: {
    emailOnReport: boolean;
    emailOnAction: boolean;
    pushNotifications: boolean;
  };
}

export type ReportReason = 
  | 'spam'
  | 'inappropriate_content'
  | 'hate_speech'
  | 'harassment'
  | 'copyright_violation'
  | 'misinformation'
  | 'violence'
  | 'self_harm'
  | 'child_safety'
  | 'other';

export type ReportStatus = 
  | 'pending'
  | 'under_review'
  | 'resolved'
  | 'dismissed'
  | 'escalated';

export type ReportPriority = 
  | 'low'
  | 'medium'
  | 'high'
  | 'urgent';

export type ReportResolution = 
  | 'content_approved'
  | 'content_removed'
  | 'user_warned'
  | 'user_suspended'
  | 'user_banned'
  | 'false_report'
  | 'duplicate_report';

export type ModerationActionType = 
  | 'hide_content'
  | 'remove_content'
  | 'warn_user'
  | 'suspend_user'
  | 'ban_user'
  | 'restrict_posting'
  | 'require_approval'
  | 'flag_for_review';

class ModerationService {
  private readonly REPORTS_COLLECTION = 'content_reports';
  private readonly BLOCKS_COLLECTION = 'user_blocks';
  private readonly ACTIONS_COLLECTION = 'moderation_actions';
  private readonly SETTINGS_COLLECTION = 'moderation_settings';

  // Report Management
  async createReport(report: Omit<ContentReport, 'id' | 'createdAt' | 'status' | 'priority'>): Promise<string> {
    try {
      const reportData = {
        ...report,
        createdAt: Timestamp.now(),
        status: 'pending' as ReportStatus,
        priority: this.calculatePriority(report.reason)
      };

      const docRef = await addDoc(collection(db, this.REPORTS_COLLECTION), reportData);
      return docRef.id;
    } catch (error) {
      console.error('Error creating report:', error);
      throw error;
    }
  }

  async getReports(filters?: {
    status?: ReportStatus;
    priority?: ReportPriority;
    reporterId?: string;
    postId?: string;
    limit?: number;
  }): Promise<ContentReport[]> {
    try {
      let q = query(collection(db, this.REPORTS_COLLECTION));

      if (filters?.status) {
        q = query(q, where('status', '==', filters.status));
      }
      if (filters?.priority) {
        q = query(q, where('priority', '==', filters.priority));
      }
      if (filters?.reporterId) {
        q = query(q, where('reporterId', '==', filters.reporterId));
      }
      if (filters?.postId) {
        q = query(q, where('postId', '==', filters.postId));
      }

      q = query(q, orderBy('createdAt', 'desc'));
      if (filters?.limit) {
        q = query(q, limit(filters.limit));
      }

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        reviewedAt: doc.data().reviewedAt?.toDate(),
      })) as ContentReport[];
    } catch (error) {
      console.error('Error getting reports:', error);
      return [];
    }
  }

  async updateReportStatus(reportId: string, status: ReportStatus, moderatorId: string, resolution?: ReportResolution): Promise<void> {
    try {
      const reportRef = doc(db, this.REPORTS_COLLECTION, reportId);
      await updateDoc(reportRef, {
        status,
        reviewedAt: Timestamp.now(),
        reviewedBy: moderatorId,
        resolution
      });
    } catch (error) {
      console.error('Error updating report status:', error);
      throw error;
    }
  }

  // User Blocking
  async blockUser(blockerId: string, blockedUserId: string, reason: string, expiresAt?: Date): Promise<string> {
    try {
      const blockData = {
        blockerId,
        blockedUserId,
        reason,
        createdAt: Timestamp.now(),
        expiresAt: expiresAt ? Timestamp.fromDate(expiresAt) : null,
        isActive: true
      };

      const docRef = await addDoc(collection(db, this.BLOCKS_COLLECTION), blockData);
      return docRef.id;
    } catch (error) {
      console.error('Error blocking user:', error);
      throw error;
    }
  }

  async unblockUser(blockId: string): Promise<void> {
    try {
      const blockRef = doc(db, this.BLOCKS_COLLECTION, blockId);
      await updateDoc(blockRef, { isActive: false });
    } catch (error) {
      console.error('Error unblocking user:', error);
      throw error;
    }
  }

  async getBlockedUsers(userId: string): Promise<UserBlock[]> {
    try {
      const q = query(
        collection(db, this.BLOCKS_COLLECTION),
        where('blockerId', '==', userId),
        where('isActive', '==', true),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        expiresAt: doc.data().expiresAt?.toDate(),
      })) as UserBlock[];
    } catch (error) {
      console.error('Error getting blocked users:', error);
      return [];
    }
  }

  async isUserBlocked(blockerId: string, blockedUserId: string): Promise<boolean> {
    try {
      const q = query(
        collection(db, this.BLOCKS_COLLECTION),
        where('blockerId', '==', blockerId),
        where('blockedUserId', '==', blockedUserId),
        where('isActive', '==', true)
      );

      const snapshot = await getDocs(q);
      return !snapshot.empty;
    } catch (error) {
      console.error('Error checking if user is blocked:', error);
      return false;
    }
  }

  // Moderation Actions
  async createModerationAction(action: Omit<ModerationAction, 'id' | 'createdAt' | 'isActive'>): Promise<string> {
    try {
      const actionData = {
        ...action,
        createdAt: Timestamp.now(),
        isActive: true
      };

      const docRef = await addDoc(collection(db, this.ACTIONS_COLLECTION), actionData);
      return docRef.id;
    } catch (error) {
      console.error('Error creating moderation action:', error);
      throw error;
    }
  }

  async getModerationActions(targetId: string, targetType: 'post' | 'user' | 'comment'): Promise<ModerationAction[]> {
    try {
      const q = query(
        collection(db, this.ACTIONS_COLLECTION),
        where('targetId', '==', targetId),
        where('targetType', '==', targetType),
        where('isActive', '==', true),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        expiresAt: doc.data().expiresAt?.toDate(),
      })) as ModerationAction[];
    } catch (error) {
      console.error('Error getting moderation actions:', error);
      return [];
    }
  }

  // Moderation Settings
  async getModerationSettings(userId: string): Promise<ModerationSettings | null> {
    try {
      const settingsRef = doc(db, this.SETTINGS_COLLECTION, userId);
      const settingsSnap = await getDoc(settingsRef);
      
      if (settingsSnap.exists()) {
        return settingsSnap.data() as ModerationSettings;
      }
      
      // Return default settings if none exist
      return this.getDefaultModerationSettings(userId);
    } catch (error) {
      console.error('Error getting moderation settings:', error);
      return this.getDefaultModerationSettings(userId);
    }
  }

  async updateModerationSettings(userId: string, settings: Partial<ModerationSettings>): Promise<void> {
    try {
      const settingsRef = doc(db, this.SETTINGS_COLLECTION, userId);
      await updateDoc(settingsRef, settings);
    } catch (error) {
      console.error('Error updating moderation settings:', error);
      throw error;
    }
  }

  // Utility Methods
  private calculatePriority(reason: ReportReason): ReportPriority {
    const urgentReasons: ReportReason[] = ['violence', 'self_harm', 'child_safety'];
    const highReasons: ReportReason[] = ['hate_speech', 'harassment'];
    const mediumReasons: ReportReason[] = ['inappropriate_content', 'copyright_violation', 'misinformation'];
    
    if (urgentReasons.includes(reason)) return 'urgent';
    if (highReasons.includes(reason)) return 'high';
    if (mediumReasons.includes(reason)) return 'medium';
    return 'low';
  }

  private getDefaultModerationSettings(userId: string): ModerationSettings {
    return {
      userId,
      autoModeration: false,
      reportThreshold: 3,
      allowedContentTypes: ['text', 'image', 'video', 'link'],
      blockedKeywords: [],
      moderationLevel: 'moderate',
      notificationSettings: {
        emailOnReport: true,
        emailOnAction: true,
        pushNotifications: true
      }
    };
  }

  // Analytics
  async getModerationStats(userId: string, timeRange: 'day' | 'week' | 'month' = 'week'): Promise<{
    totalReports: number;
    pendingReports: number;
    resolvedReports: number;
    blockedUsers: number;
    moderationActions: number;
  }> {
    try {
      const reports = await this.getReports();
      const blockedUsers = await this.getBlockedUsers(userId);
      
      return {
        totalReports: reports.length,
        pendingReports: reports.filter(r => r.status === 'pending').length,
        resolvedReports: reports.filter(r => r.status === 'resolved').length,
        blockedUsers: blockedUsers.length,
        moderationActions: 0 // Would need to implement this
      };
    } catch (error) {
      console.error('Error getting moderation stats:', error);
      return {
        totalReports: 0,
        pendingReports: 0,
        resolvedReports: 0,
        blockedUsers: 0,
        moderationActions: 0
      };
    }
  }

  // Content Analysis with AI integration
  async analyzeContent(content: string): Promise<{
    isAppropriate: boolean;
    confidence: number;
    flaggedReasons: string[];
    suggestions: string[];
    riskScore: number;
    categories: string[];
  }> {
    try {
      // Simulate AI content analysis
      const analysis = await this.performContentAnalysis(content);
      return analysis;
    } catch (error) {
      console.error('Error analyzing content:', error);
      return {
        isAppropriate: true,
        confidence: 0.5,
        flaggedReasons: [],
        suggestions: ['Unable to analyze content'],
        riskScore: 0.5,
        categories: []
      };
    }
  }

  private async performContentAnalysis(content: string): Promise<{
    isAppropriate: boolean;
    confidence: number;
    flaggedReasons: string[];
    suggestions: string[];
    riskScore: number;
    categories: string[];
  }> {
    // Mock AI analysis - in production, this would call OpenAI, Google Cloud AI, etc.
    const flaggedKeywords = [
      'spam', 'scam', 'fake', 'hate', 'violence', 'harassment', 
      'inappropriate', 'offensive', 'abuse', 'threat'
    ];
    
    const contentLower = content.toLowerCase();
    const foundKeywords = flaggedKeywords.filter(keyword => 
      contentLower.includes(keyword)
    );
    
    const riskScore = Math.min(foundKeywords.length * 0.2, 1.0);
    const isAppropriate = riskScore < 0.3;
    const confidence = Math.max(0.6, 1.0 - riskScore);
    
    const flaggedReasons = foundKeywords.map(keyword => 
      `Contains flagged keyword: ${keyword}`
    );
    
    const suggestions = [];
    if (riskScore > 0.5) {
      suggestions.push('Consider reviewing this content before publishing');
    }
    if (foundKeywords.length > 0) {
      suggestions.push('Remove or replace flagged keywords');
    }
    
    const categories = [];
    if (contentLower.includes('promotion') || contentLower.includes('sale')) {
      categories.push('promotional');
    }
    if (contentLower.includes('news') || contentLower.includes('update')) {
      categories.push('informational');
    }
    if (contentLower.includes('personal') || contentLower.includes('story')) {
      categories.push('personal');
    }
    
    return {
      isAppropriate,
      confidence,
      flaggedReasons,
      suggestions,
      riskScore,
      categories: categories.length > 0 ? categories : ['general']
    };
  }

  // Automated moderation based on rules
  async checkAutomatedModeration(content: string, settings: ModerationSettings): Promise<{
    shouldBlock: boolean;
    shouldFlag: boolean;
    reason: string;
    action: ModerationActionType | null;
  }> {
    const analysis = await this.analyzeContent(content);
    
    // Check against blocked keywords
    const hasBlockedKeywords = settings.blockedKeywords.some(keyword => 
      content.toLowerCase().includes(keyword.toLowerCase())
    );
    
    if (hasBlockedKeywords) {
      return {
        shouldBlock: true,
        shouldFlag: true,
        reason: 'Contains blocked keywords',
        action: 'remove_content'
      };
    }
    
    // Check risk score against threshold
    if (analysis.riskScore > 0.7) {
      return {
        shouldBlock: true,
        shouldFlag: true,
        reason: 'High risk content detected',
        action: 'remove_content'
      };
    }
    
    if (analysis.riskScore > 0.4) {
      return {
        shouldBlock: false,
        shouldFlag: true,
        reason: 'Content flagged for review',
        action: 'flag_for_review'
      };
    }
    
    return {
      shouldBlock: false,
      shouldFlag: false,
      reason: 'Content approved',
      action: null
    };
  }

  // Bulk moderation actions
  async bulkModeratePosts(postIds: string[], action: ModerationActionType, reason: string, moderatorId: string): Promise<void> {
    try {
      const promises = postIds.map(postId => 
        this.createModerationAction({
          moderatorId,
          targetType: 'post',
          targetId: postId,
          action,
          reason,
          details: `Bulk action: ${action}`
        })
      );
      
      await Promise.all(promises);
    } catch (error) {
      console.error('Error performing bulk moderation:', error);
      throw error;
    }
  }

  // Get moderation analytics
  async getModerationAnalytics(userId: string, timeRange: 'day' | 'week' | 'month' = 'week'): Promise<{
    totalReports: number;
    pendingReports: number;
    resolvedReports: number;
    blockedUsers: number;
    moderationActions: number;
    averageResolutionTime: number;
    topReportReasons: { reason: string; count: number }[];
    moderationTrends: { date: string; reports: number; actions: number }[];
  }> {
    try {
      const reports = await this.getReports();
      const blockedUsers = await this.getBlockedUsers(userId);
      
      // Calculate average resolution time
      const resolvedReports = reports.filter(r => r.status === 'resolved' && r.reviewedAt);
      const avgResolutionTime = resolvedReports.length > 0 
        ? resolvedReports.reduce((sum, r) => {
            const resolutionTime = r.reviewedAt!.getTime() - r.createdAt.getTime();
            return sum + resolutionTime;
          }, 0) / resolvedReports.length / (1000 * 60 * 60) // Convert to hours
        : 0;
      
      // Top report reasons
      const reasonCounts = reports.reduce((acc, report) => {
        acc[report.reason] = (acc[report.reason] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);
      
      const topReportReasons = Object.entries(reasonCounts)
        .map(([reason, count]) => ({ reason, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);
      
      // Mock trends data
      const trends = this.generateMockTrends(timeRange);
      
      return {
        totalReports: reports.length,
        pendingReports: reports.filter(r => r.status === 'pending').length,
        resolvedReports: reports.filter(r => r.status === 'resolved').length,
        blockedUsers: blockedUsers.length,
        moderationActions: 0, // Would need to implement this
        averageResolutionTime: avgResolutionTime,
        topReportReasons,
        moderationTrends: trends
      };
    } catch (error) {
      console.error('Error getting moderation analytics:', error);
      return {
        totalReports: 0,
        pendingReports: 0,
        resolvedReports: 0,
        blockedUsers: 0,
        moderationActions: 0,
        averageResolutionTime: 0,
        topReportReasons: [],
        moderationTrends: []
      };
    }
  }

  private generateMockTrends(timeRange: 'day' | 'week' | 'month'): { date: string; reports: number; actions: number }[] {
    const days = timeRange === 'day' ? 1 : timeRange === 'week' ? 7 : 30;
    const trends = [];
    
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      trends.push({
        date: date.toISOString().split('T')[0],
        reports: Math.floor(Math.random() * 10),
        actions: Math.floor(Math.random() * 8)
      });
    }
    
    return trends;
  }
}

// Create singleton instance
export const moderationService = new ModerationService();
export default moderationService;
